import PropTypes from 'prop-types';
import { Fragment, memo } from 'react';
import { twJoin } from 'tailwind-merge';

import Box from 'shopper/components/Box';
import Heading from 'shopper/components/Heading';

import toPageDescription from 'lib/htmlParsers/parsePageDescriptionHtml';
import { getDepthLevel } from 'lib/object';

import TopicPageDescription from './TopicPageDescription';

const PageDescription = ({
  className,
  headingProps = null,
  headingSizes = null,
  pageDescription = null,
  pageTitle = null,
  paragraphProps = null,
  paragraphPropsClassName,
}) => {
  const sizes = headingSizes ?? [
    { size: 'size3', as: 'h2' },
    { size: 'size4', as: 'h3' },
    { size: 'size5', as: 'h4' },
    { size: 'size6', as: 'h5' },
    { size: 'size6', as: 'h6' },
  ];
  const sharedClassName = twJoin('overflow-hidden', className);

  const mountSubtopicsRecursively = ({
    index,
    subtopics,
    paragraphs,
    title,
  }) => {
    const mergedHeadingProps = {
      ...headingProps,
      ...sizes[
        getDepthLevel({
          obj: pageDescription[index],
          key: 'title',
          value: title,
        })
      ],
    };

    return (
      <Fragment key={title}>
        <TopicPageDescription
          headingProps={title ? mergedHeadingProps : null}
          paragraphProps={paragraphProps}
          paragraphPropsClassName={paragraphPropsClassName}
          paragraphs={paragraphs}
          title={title}
        />
        {subtopics?.map((item) =>
          mountSubtopicsRecursively({ ...item, index })
        )}
      </Fragment>
    );
  };

  if (!pageDescription) {
    return null;
  }

  if (typeof pageDescription === 'string') {
    return (
      <Box className={sharedClassName} full>
        {pageTitle && (
          <Heading as="h2" className="mb-4" size="size2">
            {pageTitle}
          </Heading>
        )}
        {toPageDescription(pageDescription)}
      </Box>
    );
  }

  return (
    <Box className={sharedClassName} full>
      {pageDescription.map((item, index) =>
        mountSubtopicsRecursively({ ...item, index })
      )}
    </Box>
  );
};

PageDescription.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  headingProps: PropTypes.shape(),
  headingSizes: PropTypes.arrayOf(
    PropTypes.shape({
      [PropTypes.string]: PropTypes.shape({
        size: PropTypes.string,
        as: PropTypes.string,
      }),
    })
  ),
  pageDescription: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        paragraphs: PropTypes.arrayOf(PropTypes.string),
        subtopics: PropTypes.arrayOf(PropTypes.shape()),
      })
    ),
  ]),
  pageTitle: PropTypes.string,
  paragraphProps: PropTypes.shape(),
};

export default memo(PageDescription);
